import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
  getAllWishlist,
  addToWishlist,
  removeFromWishlist,
  clearWishlist,
  getAbandoned,
} from "../../../services/wishlist";
import { useAuth } from "../../../utils/AuthContext";

function useWishlist(shouldFetchWishlist = false) {
  const queryClient = useQueryClient();
  const WISHLIST_QUERY_KEY = ["wishlist"];

  const { isLoggedIn, user } = useAuth();

  // Get Wishlist Query - only enabled when shouldFetchWishlist is true
  const {
    data: wishlistItems,
    isLoading: isGettingWishlist,
    isError: isErrWishlist,
  } = useQuery(WISHLIST_QUERY_KEY, getAllWishlist, {
    enabled: shouldFetchWishlist && isLoggedIn,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: 3,
    onError: (error) => {
      toast.error(error.response?.data?.error || "Failed to fetch wishlist");
    },
    enabled: !!isLoggedIn,
  });

  // Add to Wishlist Mutation
  const { mutate: addToWishlistFn, isLoading: isAddingToWishlist } =
    useMutation({
      mutationFn: (productData) => {
        if (user?.role === 1) {
          throw new Error(
            "admin should not add to cart or wishlist or create order"
          );
        }
        // console.log("product data to wishlist: ", productData.user);
        return addToWishlist(productData);
      },
      // onMutate: async (productId) => {
      //   // Cancel any outgoing refetches
      //   await queryClient.cancelQueries(WISHLIST_QUERY_KEY);

      //   // Snapshot the previous value
      //   const previousWishlist = queryClient.getQueryData(WISHLIST_QUERY_KEY);

      //   // Optimistically update to the new value
      //   queryClient.setQueryData(WISHLIST_QUERY_KEY, (old) => ({
      //     ...old,
      //     items: [...(old?.items || []), { productId }],
      //   }));

      //   return { previousWishlist };
      // },
      onSuccess: () => {
        queryClient.invalidateQueries(WISHLIST_QUERY_KEY);
        toast.success("Added to wishlist");
      },
      onError: (error, productId, context) => {
        // Rollback on error
        // queryClient.setQueryData(WISHLIST_QUERY_KEY, context.previousWishlist);
        toast.error(
          error.response?.data?.error ||
            error.message ||
            "Failed to add to wishlist"
        );
      },
    });

  // Remove from Wishlist Mutation
  const { mutate: removeFromWishlistFn, isLoading: isRemovingFromWishlist } =
    useMutation({
      mutationFn: (productId) => {
        if (user?.role === 1) {
          throw new Error(
            "admin should not add to cart or wishlist or create order"
          );
        }
        return removeFromWishlist(productId);
      },
      onSuccess: () => {
        queryClient.invalidateQueries(WISHLIST_QUERY_KEY);
        toast.success("Removed from wishlist");
      },
      onError: (error) => {
        toast.error(
          error.response?.data?.error ||
            error.message ||
            "Failed to remove from wishlist"
        );
      },
    });

  // Clear Wishlist Mutation
  const { mutate: clearWishlistFn, isLoading: isClearingWishlist } =
    useMutation({
      mutationFn: clearWishlist,
      onMutate: async () => {
        await queryClient.cancelQueries(WISHLIST_QUERY_KEY);
        const previousWishlist = queryClient.getQueryData(WISHLIST_QUERY_KEY);

        queryClient.setQueryData(WISHLIST_QUERY_KEY, { items: [] });

        return { previousWishlist };
      },
      onSuccess: () => {
        queryClient.invalidateQueries(WISHLIST_QUERY_KEY);
        toast.success("Wishlist cleared successfully");
      },
      onError: (error, variables, context) => {
        queryClient.setQueryData(WISHLIST_QUERY_KEY, context.previousWishlist);
        toast.error(error.response?.data?.error || "Failed to clear wishlist");
      },
    });

  // Helper function to check if an item is in wishlist
  const isInWishlist = (productId) => {
    if (!wishlistItems) return false;

    if (wishlistItems) {
      return wishlistItems.some((item) => item?.product?._id === productId);
    }
    return wishlistItems.some((item) => item.productId === productId);
  };

  // GET ABANDONED WISHLIST
  const abandonedWishlistData = useQuery({
    queryFn: getAbandoned,
    queryKey: ["abandoned"],
    enabled: !!isLoggedIn && +user?.role === 1,
  });

  return {
    wishlistItems,
    isGettingWishlist,
    isErrWishlist,
    addToWishlistFn,
    isAddingToWishlist,
    removeFromWishlistFn,
    isRemovingFromWishlist,
    clearWishlistFn,
    isClearingWishlist,
    isInWishlist,
    // GET ABANDONED WISHLIST
    abandonedWishlistData: abandonedWishlistData.data,
    isGettingAbandonedWishlist: abandonedWishlistData.isLoading,
    isErrorGettingAbandonedWishlist: abandonedWishlistData.error,
  };
}

export default useWishlist;
